import { useMemo, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  LoaderLedgerChoice,
  LoaderLedgerConflict,
  LoaderLedgerField,
  LoaderLedgerRowPreview,
  LoaderRevUploadPreviewDto,
} from '@bhops/shared';
import { loaderRevUploadsApi } from '@/api/loaderRevUploads';
import { extractApiError } from '@/api/client';
import { Button, FormError } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Modal } from '@/components/Modal';
import { ModalSkeleton } from '@/components/ModalSkeleton';

// 로더 개조 대장(엑셀) 업로드 확인 창 — docs/plan/loader-rev-excel-upload-2026-09
//
// 올리자마자 반영하지 않는다. 파일을 고르면 서버가 **읽고 비교만** 해서 돌려주고,
// 사람이 [확정] 을 눌러야 데이터가 바뀐다(요구 3).
//
// 화면 규칙:
//   · 신규 = 초록 · 바뀌는 칸 = 노랑(옛 값 취소선 + 새 값 굵게) · 충돌 행 = 주황 테두리
//   · 변경 없음은 접어 둔다(기본 숨김, '더보기'로 펼침)
//   · 충돌은 **항목마다** 골라야 [확정] 이 켜진다(요구 4). 리포에 라디오 선례가 없어
//     `aria-pressed` 토글 버튼 묶음을 쓴다(ServiceOrdersInboxPage 관례).

type Step = 'pick' | 'uploading' | 'preview' | 'confirming' | 'done';

const CHOICE_LABELS: Record<LoaderLedgerChoice, string> = {
  KEEP_CONSOLE: '콘솔 유지',
  USE_FILE: '파일로',
  MERGE: '둘 다 병기',
};

export function LoaderRevUploadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('pick');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<LoaderRevUploadPreviewDto | null>(null);
  const [baseDate, setBaseDate] = useState('');
  const [decisions, setDecisions] = useState<Record<string, LoaderLedgerChoice>>({});
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const conflictKeys = useMemo(
    () =>
      (preview?.conflicted ?? []).flatMap((row) =>
        row.conflicts.map((c) => `${row.serial}|${c.field}`),
      ),
    [preview],
  );
  const decidedCount = conflictKeys.filter((k) => decisions[k]).length;
  const allDecided = decidedCount === conflictKeys.length;
  const nothingToDo =
    !!preview &&
    preview.counts.created === 0 &&
    preview.counts.changed === 0 &&
    preview.counts.conflicted === 0;

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const req = await loaderRevUploadsApi.requestUpload(file);
      await loaderRevUploadsApi.putFile(req.uploadUrl, file, req.headers);
      return loaderRevUploadsApi.parse(req.uploadId, req.guessedBaseDate ?? undefined);
    },
    onSuccess: (data) => {
      setPreview(data);
      setBaseDate(data.baseDate);
      setDecisions({});
      setStep('preview');
      void qc.invalidateQueries({ queryKey: ['loader-rev-uploads'] });
    },
    onError: (err) => {
      setError(extractApiError(err));
      setStep('pick');
    },
  });

  const reparseMut = useMutation({
    mutationFn: (next: string) => loaderRevUploadsApi.parse(preview!.uploadId, next),
    onSuccess: (data) => {
      setPreview(data);
      setBaseDate(data.baseDate);
      setDecisions({});
      setError(null);
    },
    onError: (err) => setError(extractApiError(err)),
  });

  const confirmMut = useMutation({
    mutationFn: () =>
      loaderRevUploadsApi.confirm(preview!.uploadId, {
        decisions: conflictKeys.map((key) => {
          const [serial, field] = key.split('|');
          return { serial, field: field as LoaderLedgerField, choice: decisions[key] };
        }),
      }),
    onSuccess: (res) => {
      setResult(
        `신규 ${res.createdLoaders}대, 수정 ${res.updatedLoaders}대, 개조 ${res.appliedRevs}건 적용` +
          (res.removedRevs ? `, ${res.removedRevs}건 해제` : ''),
      );
      setStep('done');
      // 대장 반영은 로더·개조·격자를 한꺼번에 바꾼다 — 화면 전체를 다시 읽는다.
      for (const key of [['loaders'], ['loader-revs'], ['loader-matrix'], ['loader-rev-uploads']]) {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
    onError: (err) => {
      setError(extractApiError(err));
      setStep('preview');
    },
  });

  function reset(): void {
    setStep('pick');
    setPreview(null);
    setDecisions({});
    setError(null);
    setResult(null);
    setShowUnchanged(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleClose(): void {
    if (step === 'uploading' || step === 'confirming') return; // 반영 중에는 닫지 않는다.
    // 확정하지 않고 닫으면 취소로 남긴다(무엇을 올렸다 접었는지도 기록).
    if (preview && step === 'preview') {
      void loaderRevUploadsApi.cancel(preview.uploadId).catch(() => undefined);
      void qc.invalidateQueries({ queryKey: ['loader-rev-uploads'] });
    }
    reset();
    onClose();
  }

  const busy = step === 'uploading' || step === 'confirming';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="로더 개조 이력관리대장 업로드"
      width={920}
      footer={
        step === 'preview' ? (
          <div style={footerRow}>
            <span style={footerNote}>
              {nothingToDo
                ? '이 파일로 바뀌는 것이 없습니다.'
                : `확정하면 신규 ${preview?.counts.created ?? 0}건, 변경 ${preview?.counts.changed ?? 0}건, 충돌 ${preview?.counts.conflicted ?? 0}건이 고른 대로 반영되고 확정한 사람과 시각이 기록됩니다.`}
            </span>
            <span style={{ display: 'flex', gap: 8 }}>
              <Button type="button" variant="secondary" onClick={handleClose}>
                취소
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setStep('confirming');
                  confirmMut.mutate();
                }}
                disabled={!allDecided || nothingToDo || confirmMut.isPending}
                data-testid="ledger-confirm"
                title={
                  !allDecided
                    ? `충돌 ${conflictKeys.length - decidedCount}건을 아직 고르지 않았습니다`
                    : undefined
                }
              >
                {allDecided ? '확정' : `확정 (충돌 ${conflictKeys.length - decidedCount}건 미선택)`}
              </Button>
            </span>
          </div>
        ) : step === 'done' ? (
          <div style={footerRow}>
            <span style={footerNote}>{result}</span>
            <Button type="button" onClick={handleClose}>
              닫기
            </Button>
          </div>
        ) : undefined
      }
    >
      {error && <FormError>{error}</FormError>}

      {step === 'pick' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={introText}>
            제조팀 대장 파일 <strong>로더_ASSY_이력관리대장_RevNN_YYMMDD.xlsx</strong>를 올리면 콘솔
            데이터와 비교해 <strong>무엇이 바뀌는지 먼저 보여 줍니다.</strong> 이 단계에서는
            아무것도 저장하지 않습니다.
          </p>
          <p style={introNote}>
            평소 기록은 콘솔에 먼저 하고, 대장은 주기적으로 올려 맞춥니다. 대장에 없고 콘솔에만 있는
            로더와 개조는 지우지 않습니다.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            data-testid="ledger-file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setError(null);
              setStep('uploading');
              uploadMut.mutate(file);
            }}
          />
          <div>
            <Button type="button" onClick={() => fileRef.current?.click()}>
              대장 파일 고르기
            </Button>
          </div>
        </div>
      )}

      {step === 'uploading' && (
        <div>
          <div style={introText}>파일을 올려 읽고 있습니다. 아직 저장하지는 않습니다.</div>
          <ModalSkeleton lines={5} />
        </div>
      )}

      {(step === 'preview' || step === 'confirming') && preview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={headRow}>
            <span>
              기준일{' '}
              <input
                type="date"
                value={baseDate}
                disabled={busy || reparseMut.isPending}
                onChange={(e) => {
                  setBaseDate(e.target.value);
                  if (e.target.value) reparseMut.mutate(e.target.value);
                }}
                style={dateInput}
                data-testid="ledger-base-date"
              />
            </span>
            <span style={footerNote}>
              {preview.fileName} · 올린 사람 {preview.uploadedByName ?? '–'}
            </span>
          </div>
          {preview.duplicateOfConfirmedAt && (
            <div style={dupBanner} data-testid="ledger-duplicate-banner">
              같은 내용의 파일을 {preview.duplicateOfConfirmedAt.slice(0, 10)}에 이미 확정했습니다.
            </div>
          )}

          <div style={pillRow}>
            <Pill tone="new">신규 {preview.counts.created}대</Pill>
            <Pill tone="chg">변경 {preview.counts.changed}대</Pill>
            <Pill tone="conf">충돌 {preview.counts.conflicted}대</Pill>
            <Pill tone="plain">동일 {preview.counts.unchanged}대</Pill>
            {preview.counts.excluded > 0 && (
              <Pill tone="skip">제외 {preview.counts.excluded}건</Pill>
            )}
          </div>

          {preview.counts.conflicted > 0 && (
            <section>
              <SectionTitle warn>
                콘솔에 더 최신 정보가 있는 로더 {preview.counts.conflicted}대, {decidedCount}/
                {conflictKeys.length} 선택
              </SectionTitle>
              <div style={warnNote}>
                이 로더들은 올린 파일의 기준일인 {baseDate} 이후에 콘솔에서 바뀐 이력이 있습니다.
                파일 값으로 덮어쓰면 콘솔의 최신 값이 사라집니다.
              </div>
              <div style={{ marginBottom: 6 }}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busy}
                  onClick={() =>
                    setDecisions(
                      Object.fromEntries(conflictKeys.map((k) => [k, 'KEEP_CONSOLE' as const])),
                    )
                  }
                >
                  모두 콘솔 유지
                </Button>
              </div>
              {preview.conflicted.map((row) => (
                <ConflictRow
                  key={row.serial}
                  row={row}
                  decisions={decisions}
                  disabled={busy}
                  onPick={(key, choice) => setDecisions((d) => ({ ...d, [key]: choice }))}
                />
              ))}
            </section>
          )}

          {preview.counts.created > 0 && (
            <section>
              <SectionTitle>신규 로더 {preview.counts.created}대</SectionTitle>
              {preview.created.map((row) => (
                <div key={row.serial} style={{ ...rowBox, background: 'var(--bg-success-subtle)' }}>
                  <span style={serialText}>{row.serial}</span>
                  <span style={{ fontSize: 12 }}>{row.newSummary}</span>
                </div>
              ))}
            </section>
          )}

          {preview.counts.changed > 0 && (
            <section>
              <SectionTitle>내용이 바뀌는 로더 {preview.counts.changed}대</SectionTitle>
              {preview.changed.map((row) => (
                <div key={row.serial} style={rowBox}>
                  <span style={serialText}>{row.serial}</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {row.changes.map((c) => (
                      <div key={c.field} style={changeCell}>
                        <span style={fieldLabel}>{c.label}</span>
                        <span style={oldValue}>{c.consoleValue}</span>
                        <span style={newValue}>{c.fileValue}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}

          {preview.counts.unchanged > 0 && (
            <button type="button" style={foldBtn} onClick={() => setShowUnchanged((v) => !v)}>
              변경 없음 {preview.counts.unchanged}대 {showUnchanged ? '접기' : '더보기'}
              <Icon name={showUnchanged ? 'chev-up' : 'chev-down'} size={12} aria-hidden />
            </button>
          )}
          {showUnchanged && (
            <div style={foldBody}>{preview.unchangedSerials.join(', ') || '없음'}</div>
          )}

          {preview.counts.excluded > 0 && (
            <section>
              <SectionTitle danger>제외된 행 {preview.counts.excluded}건</SectionTitle>
              {preview.excluded.map((x, i) => (
                <div key={`${x.sheet}-${x.row}-${i}`} style={excludedRow}>
                  <span style={fieldLabel}>
                    {x.sheet}
                    {x.row ? ` ${x.row}행` : ''}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{x.value}</span>
                  <span style={{ color: 'var(--fg-muted)' }}>{x.reason}</span>
                </div>
              ))}
            </section>
          )}

          {preview.counts.notInLedger > 0 && (
            <div style={foldBody}>
              대장에 없고 콘솔에만 있는 로더 {preview.counts.notInLedger}대는 그대로 둡니다. 대장을
              고칠 때 참고해 주세요. {preview.notInLedgerSerials.slice(0, 30).join(', ')}
              {preview.counts.notInLedger > 30 ? ' 외' : ''}
            </div>
          )}

          {step === 'confirming' && <div style={introText}>반영하는 중입니다…</div>}
        </div>
      )}

      {step === 'done' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ ...rowBox, background: 'var(--bg-success-subtle)' }}>
            <strong>반영을 마쳤습니다.</strong>
            <span>{result}</span>
          </div>
          <p style={introNote}>확정한 사람과 시각은 감사 기록과 업로드 이력에 남았습니다.</p>
        </div>
      )}
    </Modal>
  );
}

function ConflictRow({
  row,
  decisions,
  disabled,
  onPick,
}: {
  row: LoaderLedgerRowPreview;
  decisions: Record<string, LoaderLedgerChoice>;
  disabled: boolean;
  onPick: (key: string, choice: LoaderLedgerChoice) => void;
}) {
  return (
    <div style={conflictBox} data-testid="ledger-conflict-row">
      <span style={serialText}>{row.serial}</span>
      {row.conflicts.map((c: LoaderLedgerConflict) => {
        const key = `${row.serial}|${c.field}`;
        const picked = decisions[key];
        return (
          <div key={c.field} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={changeCell}>
              <span style={fieldLabel}>{c.label}</span>
              <span style={oldValue}>{c.consoleValue}</span>
              <span style={newValue}>{c.fileValue}</span>
            </div>
            <div style={reasonText}>{c.reason}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {c.choices.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  disabled={disabled}
                  aria-pressed={picked === choice}
                  onClick={() => onPick(key, choice)}
                  style={{
                    ...choiceBtn,
                    ...(picked === choice ? choiceBtnOn : null),
                  }}
                >
                  {CHOICE_LABELS[choice]}
                </button>
              ))}
              {c.mergePreview && picked === 'MERGE' && (
                <span style={mergePreviewText}>{c.mergePreview}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SectionTitle({
  children,
  warn,
  danger,
}: {
  children: React.ReactNode;
  warn?: boolean;
  danger?: boolean;
}) {
  return (
    <div
      style={{
        fontWeight: 700,
        fontSize: 13,
        marginBottom: 6,
        color: warn ? 'var(--fg-on-warning-tint)' : danger ? 'var(--fg-danger)' : undefined,
      }}
    >
      {children}
    </div>
  );
}

function Pill({ children, tone }: { children: React.ReactNode; tone: string }) {
  const bg =
    tone === 'new'
      ? 'var(--bg-success-subtle)'
      : tone === 'chg'
        ? 'var(--bg-warning-subtle)'
        : tone === 'conf'
          ? 'var(--bg-warning-subtle)'
          : tone === 'skip'
            ? 'var(--bg-danger-subtle)'
            : 'var(--bg-sunken)';
  return <span style={{ ...pill, background: bg }}>{children}</span>;
}

// ── 스타일 ─────────────────────────────
const introText: CSSProperties = { fontSize: 13, margin: 0, lineHeight: 1.6 };
const introNote: CSSProperties = { fontSize: 12, color: 'var(--fg-muted)', margin: 0 };
const headRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  fontSize: 12,
  flexWrap: 'wrap',
};
const dateInput: CSSProperties = {
  height: 28,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  padding: '0 6px',
  marginLeft: 4,
};
const dupBanner: CSSProperties = {
  background: 'var(--bg-info-subtle)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 10px',
  fontSize: 12,
};
const pillRow: CSSProperties = { display: 'flex', gap: 6, flexWrap: 'wrap' };
const pill: CSSProperties = {
  fontSize: 11.5,
  padding: '3px 10px',
  borderRadius: 999,
  fontWeight: 600,
};
const rowBox: CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'baseline',
  padding: '6px 8px',
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 12,
};
const conflictBox: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '8px 10px',
  marginBottom: 6,
  border: '1px solid var(--border-warning-saturated)',
  background: 'var(--bg-warning-subtle)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
};
const serialText: CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 700 };
const changeCell: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'baseline',
  flexWrap: 'wrap',
};
const fieldLabel: CSSProperties = { color: 'var(--fg-muted)', minWidth: 56 };
const oldValue: CSSProperties = { textDecoration: 'line-through', color: 'var(--fg-muted)' };
const newValue: CSSProperties = { fontWeight: 700 };
const reasonText: CSSProperties = { fontSize: 11.5, color: 'var(--fg-on-warning-tint)' };
const choiceBtn: CSSProperties = {
  height: 26,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  fontSize: 12,
  cursor: 'pointer',
};
const choiceBtnOn: CSSProperties = {
  background: 'var(--bg-selected)',
  borderColor: 'var(--brand)',
  fontWeight: 700,
};
const mergePreviewText: CSSProperties = {
  fontSize: 11.5,
  color: 'var(--fg-muted)',
  whiteSpace: 'pre-line',
};
const foldBtn: CSSProperties = {
  alignSelf: 'flex-start',
  border: '1px dashed var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--fg-muted)',
  fontSize: 12,
  padding: '6px 10px',
  cursor: 'pointer',
};
const foldBody: CSSProperties = {
  fontSize: 11.5,
  color: 'var(--fg-muted)',
  lineHeight: 1.7,
  wordBreak: 'break-all',
};
const excludedRow: CSSProperties = {
  display: 'flex',
  gap: 8,
  fontSize: 11.5,
  padding: '4px 0',
  borderBottom: '1px solid var(--border-subtle)',
  flexWrap: 'wrap',
};
const warnNote: CSSProperties = {
  fontSize: 11.5,
  color: 'var(--fg-on-warning-tint)',
  border: '1px solid var(--border-warning-saturated)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 8px',
  marginBottom: 8,
};
const footerRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  width: '100%',
};
const footerNote: CSSProperties = { fontSize: 11.5, color: 'var(--fg-muted)' };
